import { InfiniteData, QueryClient, useInfiniteQuery } from '@tanstack/react-query';

import { InfiniteScrollData } from '@/components/common/infinite-scroll';
import { getCoordinate, getHospitalCommonFilter, getHospitalSearchSortFilter } from '@/parsers/f2f/common';
import * as Types from '@/types/bff/gql-schema';
import { GraphqlError } from '@/types/error';
import { HospitalSearchQueries, MedicineType, SearchGenerallyHospitalVo } from '@/types/hospital';

import { SearchHospitalGenerallyQuery } from '@/types/bff/gql-schema';
import { getDiagnosisStateOfOperatingStatus } from '@/utils/format';
import { getFormattedReviewTotalCount } from '@/utils/format';
import { getReviewScore } from '@/utils/format';
import { useSearchHospitalGenerallyQuery } from '../list/hospitalList.gql.impl';

const size = 30;

const parseSearchHospitalGenerallyVo = (
  data: InfiniteData<SearchHospitalGenerallyQuery>
): InfiniteData<SearchGenerallyHospitalVo[]> => {
  return {
    ...data,
    pages: data.pages.map(
      (page) =>
        page.searchHospitalGenerally?.elements.map((hospital) => ({
          ...hospital,
          hospitalRepresentativeDepartmentName: hospital.hospitalRepresentativeDepartmentName ?? '',
          hospitalTotalCount: page.searchHospitalGenerally?.totalCount ?? 0,
          operationDisplay: hospital.operationDisplay ?? '',
          reviewScore: getReviewScore(hospital.reviewAverageRating),
          reviewCount: getFormattedReviewTotalCount(hospital.reviewTotalCount),
          hospitalOperationStatus: getDiagnosisStateOfOperatingStatus(hospital.hospitalOperationStatus),
          doctors: hospital.doctors
            ?.filter((doctor) => !!doctor.detail?.contractType)
            .map((doctor) => ({
              publicId: doctor.publicId,
              hospitalName: doctor.hospital.hospitalName,
              contractType: doctor!.detail!.contractType,
              readyForVideoCall: doctor.readyForVideoCall,
              doctorName: `${doctor.name} ${doctor.medicineType === MedicineType.OrientalDoctor ? '한의사' : '의사'}`,
              profileImageUrl: doctor.detail?.profileImageUrl ?? '',
              reviewAverageRating: getReviewScore(doctor.reviewAverageRating),
              reviewTotalCount: doctor.reviewTotalCount,

              professionalDepartmentName: doctor.departments.find((department) => department.isProfessional)?.name,
            })),
        })) ?? []
    ),
  };
};

interface SearchHospitalsInfinityQuery extends InfiniteScrollData<SearchGenerallyHospitalVo[]> {
  remove: () => void;
}
const useSearchHospitalsGenerallyInfinityQuery = (
  request: Types.HospitalGeneralSearchRequestInput
): SearchHospitalsInfinityQuery => {
  const queryRequest: Types.HospitalGeneralSearchRequestInput = {
    ...request,
    keyword: decodeURIComponent(request.keyword ?? ''),
  };
  const infinityQuery = useInfiniteQuery<Types.SearchHospitalGenerallyQuery, GraphqlError, SearchGenerallyHospitalVo[]>(
    useSearchHospitalGenerallyQuery.getKey({
      request: { ...queryRequest },
    }),
    ({
      pageParam,
    }: {
      pageParam?: {
        pivot?: string;
        pivotScore?: string;
      };
    }) => {
      return useSearchHospitalGenerallyQuery.fetcher({
        request: {
          ...queryRequest,
          pivotId: pageParam?.pivot,
          pivotScore: pageParam?.pivotScore,
        },
      })();
    },
    {
      enabled: !!queryRequest.keyword,
      select: parseSearchHospitalGenerallyVo,
      getNextPageParam: (lastPage: Types.SearchHospitalGenerallyQuery) => {
        const lastPageLength = lastPage.searchHospitalGenerally?.elements.length ?? 0;

        if (lastPageLength === 0) {
          return undefined;
        }

        const nextPivotId = String(lastPage.searchHospitalGenerally?.pivot);
        const nextPivotScore = String(lastPage.searchHospitalGenerally?.pivotScore);

        return { pivot: nextPivotId, pivotScore: nextPivotScore };
      },
    }
  );

  const hospitals = infinityQuery.data?.pages.flatMap((hospital) => hospital) ?? [];

  return { ...infinityQuery, data: hospitals };
};

export default useSearchHospitalsGenerallyInfinityQuery;

useSearchHospitalsGenerallyInfinityQuery.prefetchQuery = async (
  queryClient: QueryClient,
  queries: HospitalSearchQueries
) => {
  const { latitude, longitude, keyword = '', sortType, commonFilter } = queries;

  const sortTypeFilter = getHospitalSearchSortFilter(sortType);
  const hospitalCommonFilter = getHospitalCommonFilter(commonFilter);

  const queryRequest: Types.HospitalGeneralSearchRequestInput = {
    geoPoint: getCoordinate(latitude, longitude),
    limit: size,
    keyword: decodeURIComponent(keyword),
    sortType: sortTypeFilter.dtoType,
    commonFilter: hospitalCommonFilter.values,
  };

  await queryClient.prefetchInfiniteQuery(
    useSearchHospitalGenerallyQuery.getKey({
      request: queryRequest,
    }),
    useSearchHospitalGenerallyQuery.fetcher({ request: queryRequest })
  );
};
